import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../context/auth';
import { Box, CircularProgress, Dialog, DialogContent, DialogTitle, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField } from '@mui/material';
import { Add, Delete, Edit, Update, Visibility } from '@mui/icons-material';
import { ConfirmDelete } from '../../components/ConfirmDelete';
import moment from 'moment';
import { deleteGanancias, getGanancias } from '../../api/resumenGanancias';
import CreateResumenGanancias from './_Create';
import EditGanancias from './_Edit';
import ViewGanancias from './_ViewById';

const ResumenGanancias = () => {
  const [resumenGanancias, setResumenGanancias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCreate, setOpenCreate] = useState(false);
  const [openView, setOpenView] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [selectedGanancias, setSelectedGanancias] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [openConfirmDelete, setOpenConfirmDelete] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const { permisos } = useAuth();
  const { Prm_ResumenGanan_Ver, Prm_ResumenGanan_Crear, Prm_ResumenGanan_Modificar, Prm_ResumenGanan_Eliminar } = permisos;

  const handleOpenDeleteConfirm = useCallback((id: number) => {
    setDeleteId(id);
    setOpenConfirmDelete(true);
  }, []);

  const handleCloseDeleteConfirm = useCallback(() => {
    setOpenConfirmDelete(false);
    setDeleteId(null);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (deleteId !== null) {
      await deleteGanancias(deleteId);
      fetchGanancias();
      handleCloseDeleteConfirm();
    }
  }, [deleteId, handleCloseDeleteConfirm]);

  useEffect(() => {
    fetchGanancias();
  }, []);

  const fetchGanancias = useCallback(async () => {
    setLoading(true);
    const response = await getGanancias();
    if (response.isOk) {
      setResumenGanancias(response.data);
    }
    setLoading(false);
  }, []);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  }, []);

  const handleChangePage = useCallback((event: unknown, newPage: number) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  const filteredGanancias = resumenGanancias.filter((ganancias: any) => ganancias.Pr_Nombre && ganancias.Pr_Nombre.toLowerCase().includes(searchTerm.toLowerCase()));

  return (
    <div style={{ marginTop: '5px' }}>
      {loading ? (
        <Box display='flex' justifyContent='center' alignItems='center' height='100%'>
          <CircularProgress />
        </Box>
      ) : (
        <div style={{ marginTop: '5px' }}>
          <Box display='flex' justifyContent='space-between' alignItems='center' mb={2}>
            <TextField label='Buscar' value={searchTerm} onChange={handleSearchChange} fullWidth size='small' />
            {Prm_ResumenGanan_Crear === 1 && (
              <IconButton onClick={() => setOpenCreate(true)} title='Nuevo' className='tool-btn'>
                <Add color='success' />
              </IconButton>
            )}
            <IconButton onClick={fetchGanancias} title='Actualizar' className='tool-btn'>
              <Update />
            </IconButton>
          </Box>
          <TableContainer component={Paper}>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TableCell align='center'>ID</TableCell>
                  <TableCell align='center'>Fecha</TableCell>
                  <TableCell align='center'>Producto</TableCell>
                  <TableCell align='center'>Subtotal</TableCell>
                  <TableCell align='center'>Iva</TableCell>
                  <TableCell align='center'>Total</TableCell>
                  <TableCell align='center'>Mano de obra</TableCell>
                  <TableCell align='center'>Gasto adicional</TableCell>
                  <TableCell align='center'>Porcentaje de utilidad</TableCell>
                  <TableCell align='center'>Utilidad</TableCell>
                  <TableCell align='center'>Subtotal</TableCell>
                  <TableCell align='center'>Iva</TableCell>
                  <TableCell align='center'>Total</TableCell>
                  <TableCell align='center'>Tipo Cliente</TableCell>
                  <TableCell align='center'>Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredGanancias.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((ganancias: any) => (
                  <TableRow key={ganancias.GanRe_Id}>
                    <TableCell align='center'>{ganancias.GanRe_Id}</TableCell>
                    <TableCell align='center'>{moment(ganancias.GanRe_Fecha).format('YYYY-MM-DD')}</TableCell>
                    <TableCell align='center'>{ganancias.Pr_Nombre}</TableCell>
                    <TableCell align='center'>{ganancias.GanRe_Sub1}</TableCell>
                    <TableCell align='center'>{ganancias.GanRe_Iva1}</TableCell>
                    <TableCell align='center'>{ganancias.GanRe_Total1}</TableCell>
                    <TableCell align='center'>{ganancias.GanRe_ManoObra}</TableCell>
                    <TableCell align='center'>{ganancias.GanRe_GastoAdicional}</TableCell>
                    <TableCell align='center'>{ganancias.GanRe_PorceUtilidad}</TableCell>
                    <TableCell align='center'>{ganancias.GanRe_Utilidad}</TableCell>
                    <TableCell align='center'>{ganancias.GanRe_SubTotal2}</TableCell>
                    <TableCell align='center'>{ganancias.GanRe_Iva2}</TableCell>
                    <TableCell align='center'>{ganancias.GanRe_Total}</TableCell>
                    <TableCell align='center'>
                      {ganancias.GanRe_TipoCliente === 1 ? 'Concesionario' : ganancias.GanRe_TipoCliente === 2 ? 'Particular' : 'Desconocido'}
                    </TableCell>
                    <TableCell align='center'>
                      <IconButton
                        onClick={() => {
                          setSelectedGanancias(ganancias);
                          setOpenView(true);
                        }}>
                        <Visibility color='info' />
                      </IconButton>
                      {Prm_ResumenGanan_Modificar === 1 && (
                        <IconButton
                          onClick={() => {
                            setSelectedGanancias(ganancias);
                            setOpenEdit(true);
                          }}>
                          <Edit color='primary' />
                        </IconButton>
                      )}
                      {Prm_ResumenGanan_Eliminar === 1 && (
                        <IconButton onClick={() => handleOpenDeleteConfirm(ganancias.GanRe_Id)} title='Eliminar'>
                          <Delete color='error' />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination component='div' count={filteredGanancias.length} page={page} onPageChange={handleChangePage} rowsPerPage={rowsPerPage} onRowsPerPageChange={handleChangeRowsPerPage} />
        </div>
      )}

      <Dialog open={openCreate} onClose={() => setOpenCreate(false)} maxWidth='md' fullWidth>
        <DialogTitle>Crear Resumenes de ganancias</DialogTitle>
        <DialogContent>
          <CreateResumenGanancias
            onClose={() => {
              setOpenCreate(false);
              fetchGanancias();
            }}
          />
        </DialogContent>
      </Dialog>
      <Dialog open={openEdit} onClose={() => setOpenEdit(false)} maxWidth='md' fullWidth>
        <DialogTitle>Editar resumenes de ganancias</DialogTitle>
        <DialogContent>
          <EditGanancias
            ganancias={selectedGanancias}
            onClose={() => {
              setOpenEdit(false);
              fetchGanancias();
            }}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={openView} onClose={() => setOpenView(false)} maxWidth='md' fullWidth>
        <DialogTitle>Ver Listado de Ganancias</DialogTitle>
        <DialogContent>
          <ViewGanancias ganancias={selectedGanancias} onClose={() => setOpenView(false)} />
        </DialogContent>
      </Dialog>

      <ConfirmDelete open={openConfirmDelete} onClose={handleCloseDeleteConfirm} onConfirm={handleConfirmDelete} message='¿Estás seguro de que deseas eliminar este resumen de ganancias?' />
    </div>
  );
};

export default ResumenGanancias;
